import { LuTrash, LuCheck, LuX } from 'react-icons/lu'
import { useRef, useState, useEffect, useLayoutEffect } from 'react'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Fade from '@mui/material/Fade'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import DialogConfirm from './DialogConfirm'
import Tooltip from '@mui/material/Tooltip'

export type TextFieldFormProps = Omit<TextFieldProps, 'onSubmit' | 'value' | 'onChange'> & {
  onSubmit?: (value: string) => void
  onChange?: (value: string) => void
  onCancel?: () => void
  onDelete?: () => void
  value?: string
  fallback?: string
  filter?: RegExp
  valid?: RegExp
  lowercase?: boolean
  submitOnBlur?: boolean
  submitOnValid?: boolean
  deleteWarning?: string
}

/**
 * One slot's props, as an object to spread.
 *
 * ⚠ This component declares `slotProps` for `input`, `htmlInput` and
 * `formHelperText`, and that declaration used to sit AFTER `{...props}` — so a
 * caller's `slotProps` was dropped whole, silently. `Node.tsx` styled its title
 * field with `slotProps={{ input: { sx: { typography: 'h1' } } }}` and got the
 * default size, which is why a node name rendered as a heading for a user who
 * could not rename it and as a small field for one who could.
 *
 * MUI also accepts a callback per slot. Nothing here passes one, and resolving
 * it needs an ownerState this component does not hold — so object form merges
 * and a callback is ignored. Do not pass one.
 */
const slotObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

const TextFieldForm = ({
  value = '',
  fallback,
  onSubmit,
  onChange,
  onCancel,
  onDelete,
  submitOnBlur,
  submitOnValid,
  lowercase,
  filter,
  deleteWarning,
  valid,
  tabIndex,
  sx,
  slotProps,
  ...props
}: TextFieldFormProps) => {
  const [form, setForm] = useState<string>(value)
  const [waiting, setWaiting] = useState(false)
  const [focused, setFocused] = useState(false)
  const fieldRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const blocking = useRef(false)
  const cursorPositionRef = useRef<{ start: number | null; end: number | null }>({ start: null, end: null })
  const disabled = waiting || props.disabled
  const error = !!(form && valid && !valid.test(form))
  const fallbackError = !!(fallback && valid && !valid.test(fallback))
  const fallbackValid = fallbackError ? '' : fallback

  useEffect(() => {
    setForm(value)
  }, [value])

  useEffect(() => {
    if (fieldRef.current) fieldRef.current.dispatchEvent(new Event('input', { bubbles: true }))
  }, [])

  useLayoutEffect(() => {
    if (inputRef.current && cursorPositionRef.current.start !== null) {
      inputRef.current.setSelectionRange(cursorPositionRef.current.start, cursorPositionRef.current.end)
      cursorPositionRef.current = { start: null, end: null }
    }
  }, [form])

  const focus = () => {
    setFocused(true)
  }

  const blur = () => {
    fieldRef.current?.blur()
    setFocused(false)
    onCancel?.()
  }

  const cancel = () => {
    if (blocking.current) {
      blocking.current = false
      return
    }
    if (submitOnBlur) submit()
    else if (submitOnValid && !error) submit()
    else setForm(value)
    blur()
  }

  const destroy = async () => {
    setWaiting(true)
    await onDelete?.()
    setWaiting(false)
  }

  const change = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Save cursor position
    cursorPositionRef.current = {
      start: event.target.selectionStart,
      end: event.target.selectionEnd,
    }

    // if key is space and filter does not allow space add a hyphen
    if (filter && event.target.value.endsWith(' ')) {
      const edit = event.target.value.slice(0, -1) + '-'
      if (filter.test(edit)) event.target.value = edit
    }

    const value = lowercase ? event.target.value.toLowerCase() : event.target.value

    // ⚠ THE FILTER MUST NOT LOCK A VALUE THAT IS ALREADY OUTSIDE IT. 22 live networks
    // carry a legacy name with a space ("Default Network"), which nothing can create
    // today — `network_create` refuses it. Under a plain filter test EVERY keystroke on
    // such a name is rejected, a backspace included, so the field reads as frozen and
    // the only way out is select-all and retype. Let the edit through while the CURRENT
    // value fails the filter: `valid` still blocks the submit until the name is a real
    // DNS label, so this loosens what can be TYPED and never what can be SAVED.
    //
    // ⚠⚠ `form` MUST BE NON-EMPTY FOR THE HATCH TO OPEN, and that guard is the whole
    // point of it. A filter anchored with `+` does not match '' — ServiceForm's
    // `REGEX_VALID_URL_CHARACTERS` is one — so an empty field would otherwise satisfy
    // `!filter.test(form)` and unlock every keystroke after it. The first blocked
    // character then keeps `form` outside the filter, and the filter never applies
    // again for the rest of the edit. An escape hatch for a STORED value the filter
    // rejects; never a way to clear the field and type past it.
    const editable = !filter || value === '' || filter.test(value) || (!!form && !filter.test(form))

    if (editable) {
      const err = valid && !valid.test(value)
      if (submitOnValid) await onSubmit?.(err ? '' : value)
      setForm(value)
      onChange?.(value)
    }
  }

  const submit = async () => {
    if (error) return
    setWaiting(true)
    await onSubmit?.(form || fallbackValid || '')
    setWaiting(false)
    blur()
  }

  return (
    <form
      style={{ width: '100%' }}
      onSubmit={event => {
        event.preventDefault()
        submit()
      }}
    >
      <TextField
        fullWidth
        autoComplete="off"
        placeholder={fallbackValid}
        {...props}
        ref={fieldRef}
        disabled={disabled}
        error={error}
        onFocus={focus}
        onBlur={cancel}
        value={form}
        onChange={change}
        sx={{
          ...sx,
          '& .MuiInputBase-root': {
            mt: props.label ? undefined : 0,
          },
        }}
        slotProps={{
          ...slotProps,
          htmlInput: {
            ...slotObject(slotProps?.htmlInput),
            tabIndex,
            ref: inputRef,
          },
          input: {
            ...slotObject(slotProps?.input),
            endAdornment: (
              <Fade in={focused} timeout={300}>
                <Stack sx={{
                  flexDirection: 'row'
                }}>
                  {onDelete && (
                    <DialogConfirm
                      onConfirm={destroy}
                      onMouseDown={() => (blocking.current = true)}
                      color="error"
                      action="Delete"
                      dialogChildren={
                        <Typography variant="body1" component="div">
                          {deleteWarning}
                        </Typography>
                      }
                    >
                      <Tooltip title="Delete" placement="top" arrow>
                        <IconButton size="small" color="info" component="div">
                          <LuTrash />
                        </IconButton>
                      </Tooltip>
                    </DialogConfirm>
                  )}
                  {onCancel && (
                    <Tooltip title="Cancel" placement="top" arrow>
                      <IconButton size="small" color="info" onClick={cancel}>
                        <LuX />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onSubmit && (
                    <Tooltip title="Submit" placement="top" arrow>
                      <IconButton
                        disabled={disabled}
                        sx={{ '*': { strokeWidth: 2 } }}
                        type="submit"
                        color={error ? 'error' : 'primary'}
                        size="small"
                        onMouseDown={() => (blocking.current = true)}
                      >
                        {error ? <LuX /> : waiting ? <CircularProgress size="1rem" /> : <LuCheck />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Fade>
            ),
          },
          formHelperText: {
            ...slotObject(slotProps?.formHelperText),
            component: 'div',
          },
        }}
      />
    </form>
  )
}

export default TextFieldForm
